import { useState } from 'react';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Field } from '@cloudflare/kumo/components/field';
import { Input, Textarea } from '@cloudflare/kumo/components/input';
import { Select } from '@cloudflare/kumo/components/select';
import { Button } from '@cloudflare/kumo/components/button';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Switch } from '@cloudflare/kumo/components/switch';
import { Check, FloppyDisk } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import { ADMIN_STATUS_LABEL, EXHIBITOR_STATUS_ORDER } from '@nt-tokyo/shared';

// 親 page から渡される最低限のフィールド
interface EditableExhibitor {
  id: string;
  status: string;
  boothNumber: string | null;
  adminNotes: string | null;
  totalFee: number;
  finalVenueFee: number | null;
  finalEquipmentFee: number | null;
  finalPowerFee: number | null;
  finalTotalFee: number | null;
  isFree: boolean;
}

interface Props {
  exhibitor: EditableExhibitor;
  onUpdate: () => void;
}

// ラベルは @nt-tokyo/shared に集約。順序も EXHIBITOR_STATUS_ORDER に合わせる。
const STATUS_OPTIONS = EXHIBITOR_STATUS_ORDER.map((v) => ({
  value: v,
  label: ADMIN_STATUS_LABEL[v],
}));

export default function ExhibitorEditPanel({ exhibitor, onUpdate }: Props) {
  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-base font-semibold text-kumo-default">管理者操作</h2>
      <StatusEditor exhibitor={exhibitor} onUpdate={onUpdate} />
      <BoothEditor exhibitor={exhibitor} onUpdate={onUpdate} />
      <FeesEditor exhibitor={exhibitor} onUpdate={onUpdate} />
      <NotesEditor exhibitor={exhibitor} onUpdate={onUpdate} />
    </div>
  );
}

// ─── Status ────────────────────────────────────────────────
function StatusEditor({ exhibitor, onUpdate }: Props) {
  const [status, setStatus] = useState(exhibitor.status);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'ok' | 'err'; msg: string } | null>(null);

  const dirty = status !== exhibitor.status;

  const save = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      await api.patch(`/api/admin/exhibitors/${exhibitor.id}/status`, { status, reason });
      setFeedback({ type: 'ok', msg: 'ステータスを更新しました' });
      onUpdate();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'ステータスの更新に失敗しました';
      setFeedback({ type: 'err', msg: message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Surface className="p-4">
      <h3 className="mb-3 text-sm font-semibold text-kumo-default">ステータス</h3>
      <div className="flex flex-col gap-3">
        <Select<string>
          value={status}
          onValueChange={(v) => v && setStatus(v)}
          renderValue={(v) =>
            STATUS_OPTIONS.find((o) => o.value === v)?.label ?? v ?? '-'
          }
        >
          {STATUS_OPTIONS.map((o) => (
            <Select.Option key={o.value} value={o.value}>
              {o.label}
            </Select.Option>
          ))}
        </Select>
        <Field label="理由 (admin_actions に記録)">
          <Input
            placeholder="例: 内容確認完了 / 支払い確認 / 出展者からのキャンセル依頼"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
          />
        </Field>
        {status === 'waitingForPay' && exhibitor.status !== 'waitingForPay' ? (
          <Banner variant="alert">
            ステータスを「支払い待ち」に変更しても、出展者へのメールは自動送信されません。
            請求メールは「支払い管理」画面から送信してください。
          </Banner>
        ) : null}
        {feedback ? (
          <Banner variant={feedback.type === 'ok' ? 'default' : 'error'}>{feedback.msg}</Banner>
        ) : null}
        <div>
          <Button
            variant="primary"
            icon={Check}
            disabled={!dirty}
            loading={busy}
            onClick={save}
          >
            ステータスを更新
          </Button>
        </div>
      </div>
    </Surface>
  );
}

// ─── Booth ─────────────────────────────────────────────────
function BoothEditor({ exhibitor, onUpdate }: Props) {
  const [boothNumber, setBoothNumber] = useState(exhibitor.boothNumber ?? '');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'ok' | 'err'; msg: string } | null>(null);

  const dirty = (exhibitor.boothNumber ?? '') !== boothNumber;

  const save = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      await api.patch(`/api/admin/exhibitors/${exhibitor.id}`, { boothNumber });
      setFeedback({ type: 'ok', msg: '配置を更新しました' });
      onUpdate();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        '配置の更新に失敗しました';
      setFeedback({ type: 'err', msg: message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Surface className="p-4">
      <h3 className="mb-3 text-sm font-semibold text-kumo-default">ブース番号</h3>
      <div className="flex flex-col gap-3">
        <Field label="例: A-12">
          <Input
            placeholder="未割当の場合は空欄"
            value={boothNumber}
            onChange={(e) => setBoothNumber(e.currentTarget.value)}
          />
        </Field>
        {feedback ? (
          <Banner variant={feedback.type === 'ok' ? 'default' : 'error'}>{feedback.msg}</Banner>
        ) : null}
        <div>
          <Button variant="primary" icon={FloppyDisk} disabled={!dirty} loading={busy} onClick={save}>
            保存
          </Button>
        </div>
      </div>
    </Surface>
  );
}

// ─── Fees override ────────────────────────────────────────
function FeesEditor({ exhibitor, onUpdate }: Props) {
  const [isFree, setIsFree] = useState(exhibitor.isFree);
  const [finalVenueFee, setFinalVenueFee] = useState(
    exhibitor.finalVenueFee !== null ? String(exhibitor.finalVenueFee) : ''
  );
  const [finalEquipmentFee, setFinalEquipmentFee] = useState(
    exhibitor.finalEquipmentFee !== null ? String(exhibitor.finalEquipmentFee) : ''
  );
  const [finalPowerFee, setFinalPowerFee] = useState(
    exhibitor.finalPowerFee !== null ? String(exhibitor.finalPowerFee) : ''
  );
  const [finalTotalFee, setFinalTotalFee] = useState(
    exhibitor.finalTotalFee !== null ? String(exhibitor.finalTotalFee) : ''
  );
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'ok' | 'err'; msg: string } | null>(null);

  const parseOrNull = (v: string): number | null => {
    if (!v.trim()) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const save = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      await api.patch(`/api/admin/exhibitors/${exhibitor.id}/fees`, {
        finalVenueFee: parseOrNull(finalVenueFee),
        finalEquipmentFee: parseOrNull(finalEquipmentFee),
        finalPowerFee: parseOrNull(finalPowerFee),
        finalTotalFee: parseOrNull(finalTotalFee),
        isFree,
      });
      setFeedback({ type: 'ok', msg: '料金を更新しました' });
      onUpdate();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        '料金の更新に失敗しました';
      setFeedback({ type: 'err', msg: message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Surface className="p-4">
      <h3 className="mb-3 text-sm font-semibold text-kumo-default">料金 override</h3>
      <p className="mb-3 text-xs text-kumo-muted">
        申込時の自動計算結果を上書きする値を入力します。空欄なら自動計算値 (totalFee = ¥
        {exhibitor.totalFee.toLocaleString()}) が請求対象になります。
      </p>
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="会場費 (final_venue_fee)">
            <Input
              type="number"
              placeholder="自動計算"
              value={finalVenueFee}
              onChange={(e) => setFinalVenueFee(e.currentTarget.value)}
            />
          </Field>
          <Field label="備品費 (final_equipment_fee)">
            <Input
              type="number"
              placeholder="自動計算"
              value={finalEquipmentFee}
              onChange={(e) => setFinalEquipmentFee(e.currentTarget.value)}
            />
          </Field>
          <Field label="電源費 (final_power_fee)">
            <Input
              type="number"
              placeholder="自動計算"
              value={finalPowerFee}
              onChange={(e) => setFinalPowerFee(e.currentTarget.value)}
            />
          </Field>
          <Field label="合計 (final_total_fee)">
            <Input
              type="number"
              placeholder="個別 final 値の合計を上書きしたい時のみ"
              value={finalTotalFee}
              onChange={(e) => setFinalTotalFee(e.currentTarget.value)}
            />
          </Field>
        </div>
        <Field label="全額無料 (is_free)" controlFirst>
          <Switch checked={isFree} onCheckedChange={setIsFree} />
        </Field>
        {feedback ? (
          <Banner variant={feedback.type === 'ok' ? 'default' : 'error'}>{feedback.msg}</Banner>
        ) : null}
        <div>
          <Button variant="primary" icon={FloppyDisk} loading={busy} onClick={save}>
            料金を保存
          </Button>
        </div>
      </div>
    </Surface>
  );
}

// ─── Admin notes ──────────────────────────────────────────
function NotesEditor({ exhibitor, onUpdate }: Props) {
  const [notes, setNotes] = useState(exhibitor.adminNotes ?? '');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'ok' | 'err'; msg: string } | null>(null);

  const dirty = (exhibitor.adminNotes ?? '') !== notes;

  const save = async () => {
    setBusy(true);
    setFeedback(null);
    try {
      await api.patch(`/api/admin/exhibitors/${exhibitor.id}`, { adminNotes: notes });
      setFeedback({ type: 'ok', msg: 'メモを更新しました' });
      onUpdate();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        'メモの更新に失敗しました';
      setFeedback({ type: 'err', msg: message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Surface className="p-4">
      <h3 className="mb-3 text-sm font-semibold text-kumo-default">管理者メモ</h3>
      <div className="flex flex-col gap-3">
        <Textarea
          placeholder="出展者には公開しない管理者向けメモ"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          rows={4}
        />
        {feedback ? (
          <Banner variant={feedback.type === 'ok' ? 'default' : 'error'}>{feedback.msg}</Banner>
        ) : null}
        <div>
          <Button variant="primary" icon={FloppyDisk} disabled={!dirty} loading={busy} onClick={save}>
            メモを保存
          </Button>
        </div>
      </div>
    </Surface>
  );
}
